// Marketplace buy/sell history — the data behind the History tab.
//
// Now wired to the real thing: the browser extension captures your trades from
// the marketplace and pushes them to the tracker, and this reads them back. The
// entry shape below is the contract between the two, so consumers did not change
// when the sample data was removed.
//
// Entry shape:
//   id            unique within a market
//   market        marketplace id, matches MARKETPLACES[].id
//   direction     'purchase' | 'sale'
//   skin          base name without the condition, e.g. 'AK-47 | Redline'
//   condition     tracker-style condition, e.g. 'Field Tested' (see CONDITION_MAP)
//   float         wear value, or null
//   paintSeed     pattern index, or null
//   stickers      applied stickers, in slot order:
//                   name   full market name, e.g. 'Sticker | Titan (Holo) | Katowice 2014'
//                   slot   0-based position on the weapon (0 is leftmost)
//                   price  USD value of the sticker at trade time, or null
//                   wear   scrape fraction 0..1, or null for pristine
//                   image  icon URL; null falls back to a lookup in skinsData
//   price         gross USD — what was paid (purchase) or asked (sale)
//   fee           marketplace cut in USD, 0 on purchases
//   net           price - fee, i.e. cash actually moved
//   date          ISO timestamp of the trade
//   counterparty  the other side's display name, or null
//   reference     market reference price at trade time, or null
//
// The server additionally stores, for display elsewhere:
//   state                  'verified' | 'cancelled' | 'failed' | 'pending'
//   buyerSteamId / sellerSteamId
//   collection, rarity, screenshotId
//   tradeProtectionEndsAt  when the Steam trade lock expires
//
// Every state reaches this module. Cancelled and failed trades are shown in the
// ledger, marked, because a trade falling through is worth seeing. They are
// excluded from every TOTAL, because no money moved: counting a cancelled sale
// in "gross sales" would invent revenue.
import { apiFetch } from '../api';

export const MARKETPLACES = [
  { id: 'csfloat',  label: 'CSFloat',      ready: true  },
  { id: 'buff163',  label: 'Buff163',      ready: false },
  { id: 'skinport', label: 'Skinport',     ready: false },
  { id: 'csmoney',  label: 'CS.Money',     ready: false },
  { id: 'steam',    label: 'Steam Market', ready: false },
];

export const DIRECTIONS = [
  { key: 'sale',     label: 'Sold' },
  { key: 'purchase', label: 'Purchased' },
];

export function marketLabel(id) {
  return MARKETPLACES.find(m => m.id === id)?.label || id;
}

// ── Trade state ───────────────────────────────────────────────
// Only 'verified' means money actually changed hands. Everything else is a
// trade that was started and did not complete.

const COMPLETED = 'verified';

const STATE_LABELS = {
  cancelled: 'Cancelled',
  failed:    'Failed',
  pending:   'Pending',
  queued:    'Queued',
};

/** Did this trade complete? Gate every sum on this. */
export function isCompleted(entry) {
  // Entries captured before `state` existed are treated as completed: they were
  // only ever stored when a trade went through.
  return !entry?.state || entry.state === COMPLETED;
}

/** Badge text for a trade that did not complete, or null when it did. */
export function stateLabel(entry) {
  if (isCompleted(entry)) return null;
  return STATE_LABELS[entry.state] || entry.state;
}

// CSFloat charges the seller 2%, so sale rows carry a fee and purchases don't.
//
// This rate is applied here rather than upstream because CSFloat's trade API
// does not report the fee at all. The extension deliberately sends null rather
// than inventing a number, and the rate is filled in at the display layer where
// it is visible and easy to correct, instead of being baked into stored data.
const CSFLOAT_FEE_RATE = 0.02;

const FEE_RATES = { csfloat: CSFLOAT_FEE_RATE };


/**
 * Fill in what the marketplace's API did not report.
 *
 * Two gaps, both handled here so stored data stays a faithful record of what
 * the marketplace actually said:
 *
 *   fee/net   CSFloat reports neither. Derived from the known seller rate, and
 *             flagged `feeEstimated` so a consumer can say so if it wants to.
 *   stickers  `slot` may be missing. The strip sorts on it and renders slot + 1,
 *             so an absent slot would print NaN.
 */
function hydrate(entry) {
  const rate = FEE_RATES[entry.market] ?? 0;

  const isSale = entry.direction === 'sale';
  const price  = Number(entry.price) || 0;

  const feeReported = entry.fee != null;
  const fee = feeReported ? Number(entry.fee) : (isSale ? +(price * rate).toFixed(2) : 0);
  const net = entry.net != null ? Number(entry.net) : +(price - fee).toFixed(2);

  return {
    ...entry,
    fee,
    net,
    feeEstimated: !feeReported && isSale && rate > 0,
    stickers: (entry.stickers || []).map((s, i) => ({
      name:  s.name,
      slot:  Number.isFinite(Number(s.slot)) ? Number(s.slot) : i,
      price: s.price ?? null,
      wear:  s.wear ?? null,
      image: s.image ?? null,
    })),
  };
}

/**
 * Load one marketplace's completed buy/sell history for the signed-in user.
 *
 * The username comes from localStorage the same way api.js reads the session
 * token, so callers keep the single-argument signature they were written
 * against.
 *
 * @param {string} market marketplace id from MARKETPLACES
 * @returns {Promise<Array>} entries, newest first
 */
export async function fetchMarketHistory(market) {
  const username = localStorage.getItem('user');
  if (!username) return [];

  // No state filter: cancelled and failed trades belong in the ledger, marked.
  // summarize() keeps them out of the totals.
  const res = await apiFetch(
    `/api/tracker/market-history/${encodeURIComponent(username)}`
    + `?market=${encodeURIComponent(market)}`,
  );

  if (!res.ok) throw new Error(`Market history request failed: ${res.status}`);

  const { entries } = await res.json();
  return (entries || [])
    .map(hydrate)
    .sort((a, b) => (a.date < b.date ? 1 : -1));
}

/**
 * Totals for one side of the ledger, used by both skins' headline row.
 *
 * Only completed trades are counted. A cancelled sale never paid out, so
 * including it in gross sales would report revenue that does not exist. The
 * count of what fell through is returned separately so the UI can show it.
 */
export function summarize(entries) {
  const done = entries.filter(isCompleted);

  const gross = done.reduce((s, e) => s + (e.price || 0), 0);
  const fees  = done.reduce((s, e) => s + (e.fee   || 0), 0);
  const net   = done.reduce((s, e) => s + (e.net   || 0), 0);

  return {
    count: done.length,
    gross, fees, net,
    avg: done.length ? gross / done.length : null,
    largest: done.reduce((m, e) => (e.price > (m?.price ?? -Infinity) ? e : m), null),
    // Started but never completed. Shown in the ledger, absent from the money.
    cancelled: entries.length - done.length,
    total: entries.length,
  };
}

/** Difference against the market reference price at trade time, as a percent.
 *  Positive always means the trade went the user's way: under reference on a
 *  purchase, over reference on a sale. */
export function vsReference(entry) {
  if (!entry?.reference) return null;
  const diff = entry.direction === 'sale'
    ? entry.price - entry.reference
    : entry.reference - entry.price;
  return { diff, pct: (diff / entry.reference) * 100 };
}
